import { callLambdaApi } from "../callLambdaApi";
import { ApiNames, Methods } from "../constants";

/**
 * SubmissionApiCommunicator
 * 
 * Handles all API communication for submission-related operations.
 * Provides methods for CRUD operations on submissions, including status updates,
 * file management, and training submission.
 * 
 * This communicator follows the same pattern as other API communicators
 * in the application and uses the centralized callLambdaApi function.
 */
export class SubmissionApiCommunicator {

  /**
   * Submit a part vector search for future model training.
   * 
   * @param {string} partVectorSearchId - The ID of the part vector search to submit for training.
   * @returns {Promise<any>} The API response.
   */
  submitForTraining(partVectorSearchId) {
    return callLambdaApi({
      method: Methods.POST,
      apiName: ApiNames.Submissions,  
      path: this._buildBaseURL(),
      payload: { body: {partVectorSearchId: partVectorSearchId} },
    });
  }

  /**
   * Fetch a single submission by its ID.
   * 
   * @param {string} id - The submission ID.
   * @returns {Promise<import('api').GETResponse<import('models').Submission>>} The API response containing the submission.
   */
  get(id) {
    console.log("get()");
    return callLambdaApi({
      method: Methods.GET,
      apiName: ApiNames.Submissions,
      path: this._buildBaseURL() + `/${id}`,
    });
  }

  /**
   * List all submissions.
   * 
   * @returns {Promise<import('api').GETResponse<{cursor: string|null, data: import('models').Submission[]}>} The API response containing a list of submissions and a pagination cursor.
   */
  list() {
    console.log("list()");
    return callLambdaApi({
      method: Methods.GET,
      apiName: ApiNames.Submissions,
      path: this._buildBaseURL(),
    });
  }

  /**
   * Update a submission's details.
   * 
   * @param {object} requestBody - The submission update payload.
   * @param {string} requestBody.submissionId - The ID of the submission to update.
   * @param {string} requestBody.title - The updated title.
   * @param {string} requestBody.description - The updated description.
   * @param {string} requestBody.status - The updated status.
   * @param {string} requestBody.category - The updated category.
   * @param {string} requestBody.priority - The updated priority.
   * @param {Array<string>} requestBody.attachments - The updated attachments.
   * @param {string} requestBody.notes - The updated notes.
   * @returns {Promise<any>} The API response.
   */
  update(requestBody) {
    console.log("update()", requestBody);
    return callLambdaApi({
      method: Methods.POST,
      apiName: ApiNames.Submissions,
      path: this._buildBaseURL() + `/${requestBody.submissionId}`,
      payload: { body: requestBody },
    });
  }

  /**
   * Update the status of a submission.
   * 
   * @param {string} id - The submission ID.
   * @param {string} status - The new status ('approved', 'rejected', 'pending').
   * @returns {Promise<any>} The API response.
   */
  updateStatus(id, status) {
    return callLambdaApi({
      method: Methods.PUT,
      apiName: ApiNames.Submissions,
      path: this._buildBaseURL() + `/${id}`,
      payload: { body: { status } },
    });
  }

  /**
   * Delete a submission by its ID.
   * 
   * @param {string} id - The submission ID.
   * @returns {Promise<any>} The API response.
   */
  delete(id) {
    return callLambdaApi({
      method: Methods.DELETE,
      apiName: ApiNames.Submissions,
      path: this._buildBaseURL() + `/${id}`,
    });
  }

  /**
   * Returns the default serverless API endpoint for submissions.
   * @private
   * @returns {string} The base URL for submission endpoints.
   */
  _buildBaseURL() {
    return `/submission/dashboard`;
  }
} 